import React, { FC, useRef, useState } from "react";
import Ajv2020 from "ajv/dist/2020";
import styled from "styled-components";

import { formatSnakeCaseOrCamelCase } from "@formant/core/common/formatSnakeCaseOrCamelCase";
import { highlights, primary } from "@formant/ui/component/common/Colors";
import { Typography } from "@formant/ui/component/common/Typography";

import { CircleButton } from "../common/CircleButton";
import { Help } from "../common/Help";
import { Popup } from "../common/Popup";
import { Scrollable } from "../common/Scrollable";
import { StandardButton } from "../common/StandardButton";
import { getSchema } from "./GetSchema";
import {
    getArrayValue,
    isNumberString,
    isPathAnArrayIndex,
    JsonSchemaFields,
    parseCurrentDisplayPath,
} from "./JsonSchemaFields";
import { PageTransition, usePosition } from "./PageTransition";
import {
    CurrentPathSetter,
    IInputProps,
    ISchemaValidation,
    ServiceParameters,
} from "./types";
import { updatePath } from "./updatePath";

interface IJsonSchemaFormProps extends Omit<IInputProps, "validation"> {
    okText?: string;
    cancelText?: string;
    setCurrentPath: CurrentPathSetter;
    onSave: (config: string) => void;
    onCancel?: () => void;
    // optional function that displays a back button when we are
    // at the base of the schema. Runs the given function when clicked.
    onBackFromBase?: () => void;
}
export const JsonSchemaForm: FC<IJsonSchemaFormProps> = props => {
    // Path is sent to each individual form that tells them where to save/reftreive data.
    // For example a form that changes a robots name could be set to "base/robots/[0]/name"
    // Current path is a refrence to the current placement that the user is currently seeing.
    // For example "base/robots/[0]"" would show all the forms for a specfic robot, such as name,
    // birthday etc.
    const {
        schema,
        params,
        path,
        setParams,
        currentPath,
        setCurrentPath,
        onSave,
        onBackFromBase,
        onCancel,
        okText,
        cancelText,
    } = props;

    const [startingParams, setStartingParams] =
        useState<ServiceParameters>(params);

    const [validation, setValidation] = useState<ISchemaValidation | undefined>(
        undefined,
    );

    const isLastValueArrayIndex = isPathAnArrayIndex(currentPath);
    const backPath = currentPath.slice(
        0,
        currentPath.length - (isLastValueArrayIndex ? 2 : 1),
    );
    const [position, direction, onLeft, onRight] = usePosition();
    const scroller = useRef<Scrollable>(null);

    const currentPathSetter = (input: string[]) => {
        scroller?.current?.scrollToTop();
        if (currentPath.length < input.length && !isPathAnArrayIndex(input)) {
            onRight();
        }
        setCurrentPath(input);
    };

    const validateParams = (
        paramsInput: ServiceParameters,
    ): ISchemaValidation | undefined => {
        const ajv = new Ajv2020({
            strict: false,
            verbose: true,
            allErrors: true,
        });
        // only support 2020-12 validation for now.
        if (schema.$schema && !schema.$schema.includes("2020-12")) {
            return;
        }

        const validate = ajv.compile(schema);
        if (validate(paramsInput)) {
            return undefined;
        } else {
            const validationError = (validate.errors || []).pop();
            if (validationError) {
                if (validationError.instancePath === "") {
                    return undefined;
                }
                const missing = validationError.params.missingProperty;
                const errorPath = validationError.instancePath
                    .split("/")
                    .slice(1);

                if (missing) {
                    return {
                        basePath: errorPath,
                        invalidFieldName: missing,
                        exactPath: errorPath.concat(missing).join("/"),
                    };
                }

                return {
                    basePath: errorPath.slice(0, -1),
                    invalidFieldName:
                        validationError.instancePath.split("/").pop() || "",
                    exactPath: errorPath.join("/"),
                };
            }
        }

        return undefined;
    };
    const currentSchema = getSchema(schema, currentPath);

    let currentSchemaTitle = currentSchema?.title;
    if (currentSchema?.type === "array") {
        currentSchemaTitle =
            currentSchema.items["$formant.itemName"] ||
            parseCurrentDisplayPath(currentPath).pop() ||
            "";
    }

    const backTitle = formatSnakeCaseOrCamelCase(
        currentSchemaTitle || schema.title || "",
    );

    const title = formatSnakeCaseOrCamelCase(
        currentSchemaTitle || schema.title || "",
    );

    const description = currentSchema?.description || schema.description;
    const popupLevel = currentPath.filter(_ => !isNumberString(_)).length || 0;
    const popupLevels = [...Array(popupLevel).keys()];
    const displaySaveButton = currentPath.length === 0 || !!onBackFromBase;
    const displayBackButton =
        (!!onBackFromBase && currentPath.length === 0) ||
        (currentPath.length > 0 && !isPathAnArrayIndex(currentPath));

    const handleCancel = () => {
        scroller?.current?.scrollToTop();
        const arrayPath = currentPath.slice(0, currentPath.length - 1);

        // once they hit cancel, revert changes at this path
        setParams(prev =>
            updatePath(
                prev,
                arrayPath,
                getArrayValue(startingParams, arrayPath),
            ),
        );
        setCurrentPath(backPath);
        setValidation(undefined);
    };

    let isSaveButtonDisabled = false;
    let currentObject: unknown = params;
    for (const _path of currentPath) {
        currentObject = (currentObject as any)?.[_path];
    }

    let requiredProperties: string[] = [];
    if (currentSchema?.type === "array") {
        requiredProperties = currentSchema.items.required || [];
    } else {
        requiredProperties = currentSchema?.required || [];
    }

    if (
        requiredProperties.some(
            required => (currentObject as any)?.[required] === undefined,
        )
    ) {
        isSaveButtonDisabled = true;
    }

    const heading = (
        <Heading>
            {displayBackButton ? (
                <>
                    <StyledCircleButton
                        onClick={() => {
                            const shouldGoBackToBase =
                                currentPath.length === 0 && onBackFromBase;
                            if (
                                isPathAnArrayIndex(currentPath) &&
                                !shouldGoBackToBase
                            ) {
                                handleCancel();
                                return;
                            }
                            const validationResp = validateParams(params);
                            setValidation(validationResp);
                            if (validationResp !== undefined) {
                                if (
                                    validationResp.basePath.join("/") ===
                                    currentPath.join("/")
                                ) {
                                    return;
                                }
                            }

                            if (shouldGoBackToBase) {
                                onBackFromBase();
                                return;
                            }
                            scroller?.current?.scrollToTop();
                            setCurrentPath(backPath);
                            onLeft();
                        }}
                        icon="arrow-left"
                    />
                    <Title type="h3">{backTitle}</Title>
                </>
            ) : (
                <Title type="h3">
                    {popupLevel > 0 ? "Add to " : ""}
                    {title}
                </Title>
            )}
            {description && <Help text={description} size="18" />}
        </Heading>
    );

    return (
        <>
            <PageTransition
                disable={popupLevel !== 0}
                position={position}
                direction={direction}
            >
                <Container>
                    {heading}
                    <Scroll ref={scroller} key={String(direction)}>
                        <JsonSchemaFields
                            schema={schema}
                            params={params}
                            path={path}
                            setParams={setParams}
                            currentPath={currentPath}
                            setCurrentPath={currentPathSetter}
                            validation={validation}
                        />
                    </Scroll>
                    <Footer>
                        {onCancel && (
                            <CancelButton
                                onClicked={onCancel}
                                data-testid="cancel-button"
                            >
                                {cancelText || "Cancel"}
                            </CancelButton>
                        )}
                        {displaySaveButton && (
                            <DoneButton
                                onClicked={() => {
                                    const validationRep =
                                        validateParams(params);
                                    if (validationRep !== undefined) {
                                        setValidation(validationRep);
                                        return;
                                    }
                                    onSave(JSON.stringify(params));
                                    setStartingParams(params);
                                }}
                                className={
                                    popupLevel === 0 ? "hide-on-animation" : ""
                                }
                                primary={true}
                                data-testid="done-button"
                            >
                                {okText || "Done"}
                            </DoneButton>
                        )}
                    </Footer>
                </Container>
            </PageTransition>
            {popupLevels.map(_ => (
                <ArrayContainer key={_}>
                    <Popup
                        visible={true}
                        key={"popup container" + _}
                        windowClassName="popup-window"
                    >
                        <PageTransition
                            position={position}
                            direction={direction}
                            disable={[...popupLevels].pop() !== _}
                        >
                            {heading}
                            <PopupScroll ref={scroller} key={String(direction)}>
                                <JsonSchemaFields
                                    schema={schema}
                                    params={params}
                                    path={path}
                                    setParams={setParams}
                                    currentPath={currentPath}
                                    setCurrentPath={currentPathSetter}
                                    validation={validation}
                                />
                            </PopupScroll>
                            <Footer>
                                <CancelButton onClicked={handleCancel}>
                                    Cancel
                                </CancelButton>

                                <DoneButton
                                    onClicked={() => {
                                        const validationResp =
                                            validateParams(params);
                                        setValidation(validationResp);
                                        if (validationResp !== undefined) {
                                            if (
                                                validationResp.basePath.join(
                                                    "/",
                                                ) === currentPath.join("/")
                                            ) {
                                                return;
                                            }
                                        }
                                        scroller?.current?.scrollToTop();

                                        setStartingParams(params);
                                        setCurrentPath(backPath);
                                    }}
                                    disabled={isSaveButtonDisabled}
                                    primary={true}
                                >
                                    Add
                                </DoneButton>
                            </Footer>
                        </PageTransition>
                    </Popup>
                </ArrayContainer>
            ))}
        </>
    );
};

const Container = styled.div`
    padding: 0rem;
    height: 33.8125rem;
    background-color: ${primary.module};

    input {
        width: 18rem;
        max-height: 1rem;
        padding-right: 0.625rem;
        margin-top: -0.8rem;
    }
`;

const ArrayContainer = styled(Container)`
    height: 30.8125rem;
    position: fixed;
    .popup-window {
        margin-top: 1.5rem;
        height: 30.8125rem;
        width: 22.5rem;
        max-width: 50rem;
        min-height: 0rem;
        min-width: 0rem;
        padding: 0rem;
    }
`;

const StyledCircleButton = styled(CircleButton)`
    margin-right: 0.5rem;
    svg {
        fill: ${highlights.blue};
    }
    &:hover {
        box-shadow: none !important;
    }
`;

export const Heading = styled.div`
    display: flex;
    align-items: center;
    margin-left: 1.5rem;
    margin-right: 1.5rem;
    height: 4rem;
`;

export const Title = styled(Typography)`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    display: inline-block;
`;

const Footer = styled.div`
    position: relative;
    width: 100%;
    text-align: right;
`;

export const Scroll = styled(Scrollable)`
    max-height: 24.75rem;
    min-height: 24.75rem;
    overflow-y: initial !important;
`;

export const PopupScroll = styled(Scroll)`
    max-height: 21.75rem;
    min-height: 21.75rem;
    overflow-y: initial !important;
`;

const CancelButton = styled(StandardButton)`
    display: inline-block;
    margin-top: 1.5rem;
    margin-right: 0.5rem;
    min-width: 5.1875rem;
    height: 2rem;
    border-radius: 6.25rem;
`;

const DoneButton = styled(StandardButton)`
    display: inline-block;
    margin-top: 1.5rem;
    margin-right: 1rem;
    min-width: 5.1875rem;
    height: 2rem;
    border-radius: 6.25rem;
`;
